import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  ChevronLeft,
  ChevronRight,
  Save,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

interface Section {
  id: string;
  title: string;
  description: string;
  order_index: number;
  fields: Field[];
}

interface Field {
  id: string;
  field_type: string;
  label: string;
  description: string;
  required: boolean;
  options: string[];
}

interface FormData {
  [fieldId: string]: any;
}

export default function AssessmentFormFill() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState<any>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [currentSection, setCurrentSection] = useState(0);
  const [formData, setFormData] = useState<FormData>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    fetchAssessment();
  }, [assessmentId]);

  const fetchAssessment = async () => {
    try {
      const { data: assessmentData, error: assessmentError } = await supabase
        .from('member_assessments')
        .select(
          `
          *,
          template:assessment_templates(id, name, description),
          version:assessment_versions(id)
        `
        )
        .eq('id', assessmentId)
        .maybeSingle();

      if (assessmentError) throw assessmentError;
      setAssessment(assessmentData);

      if (assessmentData.status === 'assigned') {
        await supabase
          .from('member_assessments')
          .update({ status: 'in_progress', started_at: new Date().toISOString() })
          .eq('id', assessmentId);
      }

      const { data: sectionsData, error: sectionsError } = await supabase
        .from('assessment_sections')
        .select('*')
        .eq('version_id', assessmentData.version.id)
        .order('order_index');

      if (sectionsError) throw sectionsError;

      const sectionsWithFields = await Promise.all(
        (sectionsData || []).map(async (section) => {
          const { data: fieldsData, error: fieldsError } = await supabase
            .from('assessment_fields')
            .select('*')
            .eq('section_id', section.id)
            .order('order_index');

          if (fieldsError) throw fieldsError;

          return {
            ...section,
            fields: fieldsData || [],
          };
        })
      );

      setSections(sectionsWithFields);

      const { data: responsesData, error: responsesError } = await supabase
        .from('assessment_responses')
        .select('field_id, response_value')
        .eq('member_assessment_id', assessmentId);

      if (responsesError) throw responsesError;

      const existingData: FormData = {};
      (responsesData || []).forEach((response) => {
        existingData[response.field_id] = response.response_value.value;
      });
      setFormData(existingData);
    } catch (error) {
      console.error('Error fetching assessment:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateSection = () => {
    const currentFields = sections[currentSection]?.fields || [];
    const newErrors: { [key: string]: string } = {};

    currentFields.forEach((field) => {
      if (field.required && !formData[field.id]) {
        newErrors[field.id] = 'This field is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData({ ...formData, [fieldId]: value });
    if (errors[fieldId]) {
      setErrors({ ...errors, [fieldId]: '' });
    }
  };

  const saveProgress = async () => {
    setSaving(true);
    try {
      for (const [fieldId, value] of Object.entries(formData)) {
        const { data: existing } = await supabase
          .from('assessment_responses')
          .select('id')
          .eq('member_assessment_id', assessmentId)
          .eq('field_id', fieldId)
          .maybeSingle();

        if (existing) {
          await supabase
            .from('assessment_responses')
            .update({
              response_value: { value },
              updated_at: new Date().toISOString(),
            })
            .eq('id', existing.id);
        } else {
          await supabase.from('assessment_responses').insert({
            member_assessment_id: assessmentId,
            field_id: fieldId,
            response_value: { value },
          });
        }
      }
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (!validateSection()) return;

    await saveProgress();

    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (!validateSection()) return;

    await saveProgress();

    try {
      await supabase
        .from('member_assessments')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
        })
        .eq('id', assessmentId);

      navigate('/assessments/member');
    } catch (error) {
      console.error('Error submitting assessment:', error);
    }
  };

  const renderField = (field: Field) => {
    const value = formData[field.id] || '';
    const error = errors[field.id];

    const baseClasses = `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
      error ? 'border-red-500' : 'border-gray-300'
    }`;

    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <input
            type={field.field_type}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            className={baseClasses}
            placeholder={field.description}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            className={baseClasses}
            rows={4}
            placeholder={field.description}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            className={baseClasses}
            placeholder={field.description}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            className={baseClasses}
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            className={baseClasses}
          >
            <option value="">Select an option...</option>
            {field.options.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options.map((option, index) => (
              <label key={index} className="flex items-center">
                <input
                  type="radio"
                  name={field.id}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                />
                <span className="ml-2 text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            {field.options.map((option, index) => {
              const checked = Array.isArray(value) && value.includes(option);
              return (
                <label key={index} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const currentValue = Array.isArray(value) ? value : [];
                      const newValue = e.target.checked
                        ? [...currentValue, option]
                        : currentValue.filter((v) => v !== option);
                      handleFieldChange(field.id, newValue);
                    }}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="ml-2 text-gray-700">{option}</span>
                </label>
              );
            })}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (!assessment || sections.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Assessment not found</p>
          <button
            onClick={() => navigate('/assessments/member')}
            className="mt-4 text-purple-600 hover:text-purple-700"
          >
            Back to Assessments
          </button>
        </div>
      </div>
    );
  }

  const section = sections[currentSection];
  const progress = ((currentSection + 1) / sections.length) * 100;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-8 text-white">
          <h1 className="text-2xl font-bold mb-2">{assessment.template.name}</h1>
          <p className="text-purple-100">{assessment.template.description}</p>

          <div className="mt-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <span>Progress</span>
              <span>
                Section {currentSection + 1} of {sections.length}
              </span>
            </div>
            <div className="w-full bg-purple-800 rounded-full h-2">
              <div
                className="bg-white rounded-full h-2 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        <div className="p-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {section.title}
            </h2>
            {section.description && (
              <p className="text-gray-600">{section.description}</p>
            )}
          </div>

          <div className="space-y-6">
            {section.fields.map((field) => (
              <div key={field.id}>
                <label className="block mb-2">
                  <span className="text-sm font-medium text-gray-900">
                    {field.label}
                    {field.required && (
                      <span className="text-red-600 ml-1">*</span>
                    )}
                  </span>
                  {field.description && (
                    <span className="block text-sm text-gray-600 mt-1">
                      {field.description}
                    </span>
                  )}
                </label>
                {renderField(field)}
                {errors[field.id] && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors[field.id]}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentSection === 0}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </button>

            <button
              onClick={saveProgress}
              disabled={saving}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Progress'}
            </button>

            {currentSection < sections.length - 1 ? (
              <button
                onClick={handleNext}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Submit Assessment
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2">
        {sections.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSection(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentSection
                ? 'w-8 bg-purple-600'
                : index < currentSection
                ? 'bg-purple-400'
                : 'bg-gray-300'
            }`}
            title={`Section ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
